/**
 * Transient UI-interaction state — gestures currently in flight.
 *
 * Separate from `floatingPanelStore` (which owns persistent panel open/dock
 * state) because this state has no persistence and different semantics.
 *
 * Tracks transient gestures that chrome should visually yield to:
 *
 * - **Slider drag** — while a slider is being adjusted on mobile, the chrome
 *   dims so the user can see the 3D viewport. A counter (rather than a
 *   boolean) guards against races where pointer-up and pointer-cancel fire
 *   in an unexpected order, or multiple sliders interact simultaneously.
 *
 * - **Gizmo edge-pan** — while the transform gizmo's camera rig compensation
 *   is actively panning the camera to follow a near-edge drag, the chrome
 *   dims (deeper than slider-drag) to give the user an unobstructed view.
 *   A boolean, not a counter, because only one gizmo drag can be in flight
 *   at a time.
 */

import { create } from "zustand";

interface UiInteractionStore {
  sliderDragCount: number;
  beginSliderDrag: () => void;
  endSliderDrag: () => void;

  edgePanActive: boolean;
  beginEdgePan: () => void;
  endEdgePan: () => void;
}

export const useUiInteractionStore = create<UiInteractionStore>((set) => ({
  sliderDragCount: 0,
  beginSliderDrag: () =>
    set((state) => ({ sliderDragCount: state.sliderDragCount + 1 })),
  endSliderDrag: () =>
    set((state) => ({
      sliderDragCount: Math.max(0, state.sliderDragCount - 1),
    })),

  edgePanActive: false,
  beginEdgePan: () => set({ edgePanActive: true }),
  endEdgePan: () => set({ edgePanActive: false }),
}));

/** Returns `true` while at least one slider is being dragged. */
export function useIsSliderDragging(): boolean {
  return useUiInteractionStore((s) => s.sliderDragCount > 0);
}

/** Returns `true` while gizmo edge-pan camera compensation is actively running. */
export function useIsEdgePanActive(): boolean {
  return useUiInteractionStore((s) => s.edgePanActive);
}
